import * as React from "react";

import { cn } from "utils/cn";
import { DEFAULT_USAGE_LIMITS, formatPct, usageLevel, type UsageLimits } from "../format";

/** The meter's color while usage is normal; CPU and memory match their lines in the load chart. */
export type MeterColor = "chart-1" | "chart-2" | "chart-6";

type FillKey = MeterColor | "warning" | "danger";

// Full class names so Tailwind keeps them. The track is a lighter step of the fill's own color.
const FILL: Record<FillKey, string> = {
  "chart-1": "bg-chart-1",
  "chart-2": "bg-chart-2",
  "chart-6": "bg-chart-6",
  warning: "bg-warning",
  danger: "bg-danger",
};
const TRACK: Record<FillKey, string> = {
  "chart-1": "bg-chart-1/15",
  "chart-2": "bg-chart-2/15",
  "chart-6": "bg-chart-6/20",
  warning: "bg-warning/15",
  danger: "bg-danger/15",
};

interface UsageMeterProps {
  label: string;
  /** 0–100; null when the server didn't report it. */
  value: number | null;
  color: MeterColor;
  /** Context under the bar, e.g. "of 15.6 GB". */
  detail?: React.ReactNode;
  /** Warning and critical levels (SAP settings); 75% and 90% by default. */
  limits?: UsageLimits;
}

/** A server resource as a big percentage over a bar that turns amber, then red, at the limits. */
const UsageMeter: React.FC<UsageMeterProps> = ({
  label,
  value,
  color,
  detail,
  limits = DEFAULT_USAGE_LIMITS,
}) => {
  const level = usageLevel(value, limits);
  const key: FillKey = level === "normal" ? color : level;
  const reported = typeof value === "number" && Number.isFinite(value);
  const width = reported ? Math.min(Math.max(value, 0), 100) : 0;

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        {level !== "normal" && (
          <span
            className={cn(
              "text-xs font-medium",
              level === "danger" ? "text-danger-text" : "text-warning-text"
            )}
          >
            {level === "danger" ? "Critical" : "High"}
          </span>
        )}
      </div>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{formatPct(value)}</p>
      {reported ? (
        <div
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(width)}
          className={cn("mt-2 h-1.5 overflow-hidden rounded-full", TRACK[key])}
        >
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-base ease-standard",
              FILL[key]
            )}
            style={{ width: `${width}%` }}
          />
        </div>
      ) : (
        <div aria-hidden="true" className="mt-2 h-1.5 rounded-full bg-surface-muted" />
      )}
      {detail && <p className="mt-1.5 truncate text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
};

export default UsageMeter;
